module Steps.Step1.Update where

import Common

import API.Document (CopyDocRequest, FormReport, ToggleLockInput, ToggleLockResult, applyLockPermission)
import API.RecentForms (RecentForm)
import API.Search (SearchSemester, SearchTeacher)
import API.SelStudent (SelStudent)
import API.Signature (SignatureRequest)
import API.Types (DocumentID, StudentID)
import Data.Array (concatMap, filter)
import Data.Array as Arr
import Data.Generic.Rep (class Generic)
import Data.Maybe (isJust)
import GraphQL.Query (Mutation)
import Internal.SelectionMap (SelectionMap, listSelected)
import Internal.SelectionMap as SM
import Steps.FormPicker (FormPickerState)
import Steps.Step1.State (RecentFormState, applySearchChanged, selectedForms)
import Widgets.BetterSelect (setSelected)
import Widgets.Search (SearchState)
import Widgets.Signature.Types (SigRelation)
import Widgets.UpdateForm (FormsResponseDummy, updateFormsMutation)

data Msg
    = StateChanged RecentFormState
    | ViewAction RecentFormAction
    | CopyStudentsSelected (Maybe (SelectionMap StudentID SelStudent))
    | DeleteConfirmed Boolean
    | SignatureRequestsSelected (Maybe (Array SignatureRequest))
    | FormsUpdateSelected (Maybe UpdateFormsSelection)

data UpdateFormsSelection
    = UpdateSemesterSelected SearchSemester
    | UpdateTeacherSelected SearchTeacher

type DocExport = { label :: String, id :: String }

-- NOTE: Many of the export actions will likely require another widget to
-- interact with the user as to how they want to export a document.

-- | Actions which affect things beyond simple state updates.
-- |
-- | This includes things like going to another step, loading data, etc.
data RecentFormAction
    = EditDocument RecentForm
    -- ^ Edit this document.
    | EditDocuments
    -- ^ Edit all selected documents.
    | ToggleDocumentLocks
    -- ^ Toggle lock state for all selected documents.
    | CopyDocuments
    -- ^ Copy all selected documents.
    | DeleteDocuments
    -- ^ Delete all selected documents.
    | ExportDocuments DocExport
    -- ^ Export all selected documents.
    | RequestSignatures SigRelation
    -- ^ Send emails requesting signatures for all selected documents.
    | UpdateForms UpdateFormsType
    -- ^ Mass-update form properties
    | SearchChanged SearchState
    -- ^ Search state changed.
    | FormPickerChanged FormPickerState
    -- ^ Form picker state changed. This also refreshes search.
    | StartFormReorder
    | SaveFormOrder
    | ChangePage Int
    -- ^ Change to the given page.

    | CreateForms
    -- ^ Go to step 2 with whatever forms were selected in the form picker
    -- added to the work queue.

    -- ExportAllForms
    -- ^ Will either queue all forms for export or bring the student/teacher to
    -- a better prompt to handle exporting forms.

    -- SignAllDocuments
    --
    -- NOTE: There are many things to that need to be checked before we
    -- implement any functionality like this. Leaving this comment here to show
    -- that it has been considered and treaded on carefully.

    -- Form specific actions

    -- SelectFormForExport
    -- ^ Pick a form for export.
    -- ExportPDF
    -- ^ Bring us to a export screen for a specific document.
    -- DeleteDocument
    -- ^ Send a request to the API to soft delete a document.
    --
    -- NOTE: Should probably display a visual confirmation to actually delete
    -- the document and a way to undo deleting the document if desired. Maybe
    -- something like greying out a document and providing buttons to confirm
    -- and undo.
    -- ExportPDFPages
    -- ^ Prompt to export only specific pages of a document.
    -- PrintDocument
    -- ^ Bit of a confusing one, but rather than being redirected to a page that
    -- displays the PDF we'll attempt to prompt the browser to print the
    -- document.
    -- SignDocument
    -- ^ Go to a widget that allows you to sign the document.

derive instance genRFA :: Generic RecentFormAction _

data UpdateFormsType = UpdateSemester | UpdateTeacher

toggleLockRequests :: RecentFormState -> Maybe (Array ToggleLockInput)
toggleLockRequests state =
    if Arr.null selected || not (Arr.all (\form -> form.can.lock) selected)
        then Nothing
        else Just requests
  where
    selected = selectedForms state
    -- If all selected docs are locked, unlock all of them. For a mixed
    -- selection, only lock the docs that are not already locked; the backend
    -- rejects redundant lock requests for already locked docs.
    locked = Arr.all (isJust <<< _.locked_at) selected
    requests = if locked
        then map (\form -> { form_id: form.id, locked: false }) selected
        else map (\form -> { form_id: form.id, locked: true }) (filter (not isJust <<< _.locked_at) selected)

copyRequests :: RecentFormState -> SelectionMap StudentID SelStudent -> Array CopyDocRequest
copyRequests state sel = concatMap (copyReqs (listSelected sel) <<< _.id) (selectedForms state)
  where
    copyReqs :: forall arr. Functor arr => Foldable arr => arr SelStudent -> DocumentID -> Array CopyDocRequest
    copyReqs students docId = Arr.fromFoldable $ map (\s -> { form_id: docId, student_id: s.id }) students

applyFormsUpdateSelection :: UpdateFormsSelection -> RecentFormState -> RecentFormState
applyFormsUpdateSelection selection state = case selection of
    UpdateSemesterSelected semester ->
        applySearchChanged
            (state.searchState { semester = setSelected state.searchState.semester $ Just { label: semester.name, value: semester } })
            state
    UpdateTeacherSelected teacher ->
        applySearchChanged
            (state.searchState { teacher = setSelected state.searchState.teacher $ Just { label: teacher.name, value: teacher } })
            state

runUpdateFormsMutation :: UpdateFormsSelection -> Array RecentForm -> Mutation FormsResponseDummy
runUpdateFormsMutation selection = case selection of
    UpdateSemesterSelected semester -> updateFormsMutation (Left semester)
    UpdateTeacherSelected teacher -> updateFormsMutation (Right teacher)

findExportReport :: DocExport -> Array FormReport -> Maybe FormReport
findExportReport etype = find \rep -> rep.label == etype.id

-- | Following a request to backend this updates the local state.
applyToggleLockResults :: Array ToggleLockResult -> RecentFormState -> RecentFormState
applyToggleLockResults results state = state
    { recentForms = map (patchDoc results) state.recentForms
    , selection = SM.fromFoldable' $ map (patchDoc results) $ Arr.fromFoldable $ listSelected state.selection
    }
    where
        -- Patch Recent Forms' form/document with new locked state.
        -- @todo: move this to a generic function?
        patchDoc :: Array ToggleLockResult -> RecentForm -> RecentForm
        patchDoc results' form =
            maybe form (\result -> applyLockPermission result form) $
                find (\result -> result.id == form.id) results'
